import React, { useEffect, useState } from "react";
import Layout from "../../../layouts/default";
import {
    Button,
    Spinner,
    Table,
    Row,
    Col,
    Form,
    Modal,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import SmartPagination from "../../Report/EmailCampaignRunReport/DynamicSmartPagination";
import {
    createRole,
    updateRole,
    fetchRoles,
    deactivateRole,
} from "../../../store/slices/rolesAndPermissions/rolesAndPermissions";
import { toast } from "react-toastify";
import { CheckCircle, Trash } from "react-bootstrap-icons";
import { ICONS } from "../../../constants/icons";
import { useNavigate } from "react-router-dom";
import { hasPermission } from "../../../services/utils";
import { PERMISSIONS } from "../../../contants/permission";

function RolesManagement() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { rolesList, totalCount, loading } = useSelector(
        (state) => state.rolesAndPermissionReducer
    );

    const [filters, setFilters] = useState({
        name: "",
        status: "",
    });

    const [appliedFilters, setAppliedFilters] = useState({});
    const [page, setPage] = useState(1);
    const limit = 10;

    const totalPages = Math.ceil(totalCount / limit);

    // Create Role Modal
    const [showCreate, setShowCreate] = useState(false);
    const [newRole, setNewRole] = useState({
        name: "",
        status: 1,
    });

    // Edit Role Modal
    const [showEdit, setShowEdit] = useState(false);
    const [editRoleData, setEditRoleData] = useState({
        id: "",
        name: "",
        status: 1,
    });

    useEffect(() => {
        loadRoles();
    }, [appliedFilters, page]);

    const loadRoles = async () => {
        await dispatch(
            fetchRoles({
                ...appliedFilters,
                limit,
                page,
            })
        );
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const applyFilters = () => {
        setAppliedFilters(filters);
        setPage(1);
    };

    const resetFilters = () => {
        const empty = { name: "", status: "" };
        setFilters(empty);
        setAppliedFilters({});
        setPage(1);
    };

    const handleDeleteRole = async (role) => {
        try {
            const user = JSON.parse(localStorage.getItem("user"));
            const userId = user?.data?.id;

            if (typeof role?.name === "string" && role?.name?.toLowerCase() === 'super admin') {
                toast.error("You can not deactivate super admin.");
                return;
            }

            if (!window.confirm(`Deactivate "${role.name}"?`)) return;

            const res = await dispatch(
                deactivateRole({
                    id: role.id,
                    status: 0,
                    updatedBy: userId,
                })
            ).unwrap();

            if (res?.success) {
                toast.success("Role deactivated successfully");
            } else {
                toast.error(res?.message || "Failed to deactivate role");
            }

            loadRoles();
        } catch (err) {
            console.error("Error deactivating role:", err);
            toast.error("Something went wrong while deactivating role");
        }
    };


    // Create Role
    const handleCreateRole = async (e) => {
        e.preventDefault();

        try {
            const user = JSON.parse(localStorage.getItem("user"));
            const userId = user?.data?.id;

            if (!newRole.name) {
                toast.error("Role name is required");
                return;
            }

            const res = await dispatch(
                createRole({
                    name: newRole.name.trim(),
                    status: Number(newRole.status),
                    createdBy: userId,
                })
            ).unwrap();

            if (res?.success) {
                toast.success("Role created successfully");
            } else {
                toast.error(res?.message || "Failed to create role");
            }

            setShowCreate(false);
            setNewRole({ name: "", status: 1 });
            loadRoles();
        } catch (err) {
            console.error("Error creating role:", err);
            toast.error("Something went wrong while creating role");
        }
    };

    // Handle Edit
    const openEditModal = (role) => {
        setEditRoleData({
            id: role.id,
            name: role.name,
            status: role.status,
        });
        setShowEdit(true);
    };

    const handleUpdateRole = async (e) => {
        e.preventDefault();

        try {
            const user = JSON.parse(localStorage.getItem("user"));
            const userId = user?.data?.id;

            if (!editRoleData.name?.trim()) {
                toast.error("Role name is required");
                return;
            }

            if (typeof editRoleData?.name === "string" && editRoleData?.name?.toLowerCase() === 'super admin') {
                toast.error("You can not deactivate super admin.");
                return;
            }

            const res = await dispatch(
                updateRole({
                    id: editRoleData.id,
                    name: editRoleData.name.trim(),
                    status: Number(editRoleData.status),
                    updatedBy: userId,
                })
            ).unwrap();

            if (res?.success) {
                toast.success("Role updated successfully");
            } else {
                toast.error(res?.message || "Failed to update role");
            }

            setShowEdit(false);
            loadRoles();
        } catch (err) {
            console.error("Error updating role:", err);
            toast.error("Something went wrong while updating role");
        }
    };

    const handleActivateRole = async (role) => {
        try {
            const user = JSON.parse(localStorage.getItem("user"));
            const userId = user?.data?.id;

            const res = await dispatch(
                updateRole({
                    id: role.id,
                    status: 1,
                    updatedBy: userId,
                    name: role.name,
                })
            ).unwrap();

            if (res?.success) {
                toast.success("Role activated successfully");
            } else {
                toast.error(res?.message || "Failed to update role");
            }

            setShowEdit(false);
            loadRoles();
        } catch (err) {
            console.error("Error updating role:", err);
            toast.error("Something went wrong while updating role");
        }
    }

    const handleViewPermissions = (role) => {
        navigate(`permissions/view/${role.id}`, {
            state: { role },
        });
    };



    return (
        <Layout>
            <div className="d-flex justify-content-between align-items-center my-3 px-3">
                <h4 className="fw-semibold mb-0">Role Management</h4>

                {
                    hasPermission(PERMISSIONS.ROLES_MANAGEMENT.CREATE) && <Button variant="primary" onClick={() => setShowCreate(true)}>
                        + Create Role
                    </Button>
                }
            </div>

            {/* Filters Section */}
            <div className="p-3 border rounded bg-light mx-3 mb-3">
                <Row className="g-2 align-items-end">

                    {/* Role Name */}
                    <Col md={3}>
                        <Form.Group>
                            <Form.Label>Role Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={filters.name}
                                placeholder="Search name"
                                onChange={handleFilterChange}
                            />
                        </Form.Group>
                    </Col>

                    {/* Status */}
                    <Col md={2}>
                        <Form.Group>
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                name="status"
                                value={filters.status}
                                onChange={handleFilterChange}
                            >
                                <option value="">All</option>
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>

                    {/* Push Buttons to Right */}
                    <Col className="d-flex justify-content-end gap-2 ms-auto">
                        <Button className="px-4" onClick={applyFilters}>
                            Apply
                        </Button>

                        <Button variant="secondary" className="px-4" onClick={resetFilters}>
                            Reset
                        </Button>
                    </Col>

                </Row>
            </div>

            {/* Table Section */}
            <div className="mx-3">
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" />
                        <p>Loading...</p>
                    </div>
                ) : rolesList?.length ? (
                    <>
                        <Table bordered hover>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Role Name</th>
                                    <th>Status</th>
                                    <th>Created On</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>

                            <tbody>
                                {rolesList.map((role, index) => (
                                    <tr key={role.id}>
                                        <td>{(page - 1) * limit + index + 1}</td>
                                        <td>{role.name}</td>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <span
                                                    className={`badge ${role.status ? "bg-success" : "bg-danger"} px-3 py-1 me-2`}
                                                    style={{ fontSize: "0.75rem" }}
                                                >
                                                    {role.status ? "Active" : "Inactive"}
                                                </span>

                                                {role.status && role?.name?.toLowerCase() !== 'super admin' ? (
                                                    hasPermission(PERMISSIONS.ROLES_MANAGEMENT.DELETE) && <Trash
                                                        size={18}
                                                        className="text-danger-hover"
                                                        style={{
                                                            cursor: "pointer",
                                                            opacity: 0.7,
                                                            transition: "opacity 0.2s"
                                                        }}
                                                        onMouseEnter={e => e.currentTarget.style.opacity = 1}
                                                        onMouseLeave={e => e.currentTarget.style.opacity = 0.7}
                                                        title="Deactivate"
                                                        onClick={() => handleDeleteRole(role)}
                                                    />
                                                ) : (role?.name?.toLowerCase() !== 'super admin' && hasPermission(PERMISSIONS.ROLES_MANAGEMENT.EDIT) &&
                                                    <CheckCircle
                                                        size={18}
                                                        className="text-success-hover"
                                                        style={{
                                                            cursor: "pointer",
                                                            opacity: 0.7,
                                                            transition: "opacity 0.2s"
                                                        }}
                                                        onMouseEnter={e => e.currentTarget.style.opacity = 1}
                                                        onMouseLeave={e => e.currentTarget.style.opacity = 0.7}
                                                        title="Activate"
                                                        onClick={() => handleActivateRole(role)}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            {role.createdAt
                                                ? new Date(role.createdAt).toLocaleString()
                                                : "NA"}
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-1">
                                                {
                                                    role?.name?.toLowerCase() !== 'super admin' && hasPermission(PERMISSIONS.ROLES_MANAGEMENT.EDIT) && <span
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => openEditModal(role)}
                                                        title="Edit Role"
                                                    >
                                                        <img
                                                            src={ICONS.edit_action}
                                                            alt="Edit"
                                                            style={{
                                                                width: "25px",
                                                                height: "25px",
                                                                opacity: 0.7,
                                                                transition: "opacity 0.2s"
                                                            }}
                                                            onMouseEnter={e => e.currentTarget.style.opacity = 1}
                                                            onMouseLeave={e => e.currentTarget.style.opacity = 0.7}
                                                        />
                                                    </span>
                                                }
                                                &nbsp;
                                                {
                                                    hasPermission(PERMISSIONS.PERMISSIONS_MANAGEMENT.VIEW) && <span
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => handleViewPermissions(role)}
                                                        title="View Permissions"
                                                    >
                                                        <img
                                                            src={ICONS.view_action}
                                                            alt="View"
                                                            style={{
                                                                width: "25px",
                                                                height: "25px",
                                                                opacity: 0.7,
                                                                transition: "opacity 0.2s"
                                                            }}
                                                            onMouseEnter={e => e.currentTarget.style.opacity = 1}
                                                            onMouseLeave={e => e.currentTarget.style.opacity = 0.7}
                                                        />
                                                    </span>
                                                }
                                                &nbsp;
                                                {role.status === 1 && role?.name?.toLowerCase() !== 'super admin' && hasPermission(PERMISSIONS.ROLES_MANAGEMENT.DELETE) && (
                                                    <span
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => handleDeleteRole(role)}
                                                        title="Delete Role"
                                                    >
                                                        <img
                                                            src={ICONS.delete_action}
                                                            alt="Delete"
                                                            style={{
                                                                width: "25px",
                                                                height: "25px",
                                                                opacity: 0.7,
                                                                transition: "opacity 0.2s"
                                                            }}
                                                            onMouseEnter={e => e.currentTarget.style.opacity = 1}
                                                            onMouseLeave={e => e.currentTarget.style.opacity = 0.7}
                                                        />
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        <SmartPagination
                            currentPage={page}
                            totalPages={totalPages}
                            onPageChange={(p) => setPage(p)}
                        />
                    </>
                ) : (
                    <p className="text-center text-muted py-4">No roles found</p>
                )}
            </div>

            {/* Create Role Modal */}
            <Modal show={showCreate} onHide={() => setShowCreate(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Create Role</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleCreateRole}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Role Name</Form.Label>
                            <Form.Control
                                value={newRole.name}
                                onChange={(e) =>
                                    setNewRole({ ...newRole, name: e.target.value })
                                }
                                required
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                value={newRole.status}
                                onChange={(e) =>
                                    setNewRole({ ...newRole, status: Number(e.target.value) })
                                }
                            >
                                <option value={1}>Active</option>
                                <option value={0}>Inactive</option>
                            </Form.Select>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowCreate(false)}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="primary">
                            Create
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Edit Role Modal */}
            <Modal show={showEdit} onHide={() => setShowEdit(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Role</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleUpdateRole}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Role Name</Form.Label>
                            <Form.Control
                                value={editRoleData.name}
                                onChange={(e) =>
                                    setEditRoleData({ ...editRoleData, name: e.target.value })
                                }
                                required
                            />
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                value={editRoleData.status}
                                onChange={(e) =>
                                    setEditRoleData({
                                        ...editRoleData,
                                        status: Number(e.target.value),
                                    })
                                }
                            >
                                <option value={1}>Active</option>
                                <option value={0}>Inactive</option>
                            </Form.Select>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowEdit(false)}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="primary">
                            Update
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Layout>
    );
}

export default RolesManagement;
